import { BaseService } from './base-service';

import { Mosaic } from '@/core/domain/mosaic';
import { CustomReportWorstScores } from '@/core/domain';
import { CustomReport } from '@/core/domain/custom-report';
import { ReportsGenerated } from '@/core/domain/reports-generated';
import { CustomReportRiskCount } from '@/core/domain/custom-report-risk-count';

import { GetMosaicRequestDTO, GetMosaicResponseDTO } from '@/core/dto/dashboard';
import { CustomReportWorstScoresDTO, GetWorstScoresRequestDTO } from '@/core/dto';
import { CustomReportDTO, GetCustomReportListDTO } from '@/core/dto/custom-report';
import { ReportsGeneratedDTO, GetReportRequestDTO } from '@/core/dto/reports-generated';
import { EwaCustomReportResultsDTO, GetEwaCustomReportResultsRequestDTO } from '@/core/dto/ewa-custom-report';
import { CustomReportRiskCountDTO, GetCustomReportRiskCountRequestDTO } from '@/core/dto/custom-report-risk-count';
import {
	GetLastConsolidatedActionLogRequestDTO,
	GetLastConsolidatedActionLogResponseDTO
} from '@/core/dto/custom-report-last-consolidated-action-log';
import type {
	GetCustomReportsConsolidatedRequestDTO,
	CustomReportsConsolidatedResponseDTO,
	GetCustomReportStepsByResultIdRequestDTO,
	CustomReportStepsByResultIdResponseDTO,
	GetCustomReportStepKeysByResultIdRequestDTO,
	CustomReportStepKeysByResultIdResponseDTO,
	GetActionPlansRequestDTO,
	ActionPlansResponseDTO,
	GetErgonomicToolsRequestDTO,
	GetErgonomicToolsResponseDTO
	// LastConsolidatedActionLogResponseDTO
} from '@/core/dto/custom-report';

import { MosaicFindAllMapper } from '@/core/mapper/mosaic-find-all';
import { CustomReportFindAllMapper } from '@/core/mapper/custom-report-find-all';
import { CustomReportRiskCountMapper } from '@/core/mapper/custom-report-risk-count';
import { ReportsGeneratedFindOneMapper } from '@/core/mapper/reports-generated-find-one';
import { CustomReportWorstScoresMapper } from '@/core/mapper/custom-report-worst-scores';
import { GetErgonomicToolsMapper } from '@/core/mapper/get-ergonomic-tools';
import { GetCustomReportOptionsMapper } from '@/core/mapper/get-custom-report-options';

class CustomReportService extends BaseService<CustomReportDTO> {
	constructor(public readonly basePath: string = '/custom-report') {
		super();
	}

	async list(params: GetCustomReportListDTO): Promise<CustomReport[]> {
		const url = this.basePath + '/list';
		const { data } = await this.getInstance().get<CustomReportDTO>(url, { params });
		return new CustomReportFindAllMapper(data).toDomain();
	}

	async listOptions(params: GetCustomReportListDTO) {
		const url = this.basePath + '/list';
		const { data } = await this.getInstance().get<CustomReportDTO>(url, { params });
		return new GetCustomReportOptionsMapper(data).toDomain();
	}

	async getMosaic(params: GetMosaicRequestDTO): Promise<Mosaic> {
		const url = this.basePath + '/mosaic';
		const { data } = await this.getInstance().get<GetMosaicResponseDTO>(url, { params });
		return new MosaicFindAllMapper(data).toDomain();
	}

	async getChecklist(params: GetReportRequestDTO): Promise<ReportsGenerated> {
		const url = this.basePath + '/result/get-count-checklist';
		const { data } = await this.getInstance().get<ReportsGeneratedDTO>(url, { params });
		return new ReportsGeneratedFindOneMapper(data.report, data.labels).toDomain();
	}

	async getHierarchyWorstScores(params: GetWorstScoresRequestDTO): Promise<CustomReportWorstScores[][]> {
		const url = this.basePath + '/result/hierarchy-worst-scores';
		const { data } = await this.getInstance().get<CustomReportWorstScoresDTO[]>(url, { params });
		return new CustomReportWorstScoresMapper(data).toDomain();
	}

	async getCountByRisk(params: GetCustomReportRiskCountRequestDTO): Promise<CustomReportRiskCount[]> {
		const url = this.basePath + '/result/count-by-risk';
		const { data } = await this.getInstance().get<CustomReportRiskCountDTO>(url, { params });
		return new CustomReportRiskCountMapper(data).toDomain();
	}

	async findAllEwa(params: GetEwaCustomReportResultsRequestDTO): Promise<EwaCustomReportResultsDTO> {
		const url = this.basePath + '/result/ewa/';
		const { data } = await this.getInstance().get<EwaCustomReportResultsDTO>(url, { params });
		return data;
	}

	async getCustomReportsConsolidated(
		params: GetCustomReportsConsolidatedRequestDTO
	): Promise<CustomReportsConsolidatedResponseDTO> {
		const url = this.basePath + '/result/reports';
		const { data } = await this.getInstance().get<CustomReportsConsolidatedResponseDTO>(url, { params });
		return data;
	}

	async getCustomReportStepsByResultId(
		params: GetCustomReportStepsByResultIdRequestDTO
	): Promise<CustomReportStepsByResultIdResponseDTO> {
		const url = this.basePath + `/step/custom-report-sections`;
		const { data } = await this.getInstance().get<CustomReportStepsByResultIdResponseDTO>(url, { params });
		return data;
	}

	async getCustomReportStepKeysByResultId(
		params: GetCustomReportStepKeysByResultIdRequestDTO
	): Promise<CustomReportStepKeysByResultIdResponseDTO> {
		const url = this.basePath + `/step-key/custom-report-section-criteria`;
		const { data } = await this.getInstance().get<CustomReportStepKeysByResultIdResponseDTO>(url, { params });
		return data;
	}

	async getActionPlans(params: GetActionPlansRequestDTO): Promise<ActionPlansResponseDTO> {
		const url = this.basePath + '/result/action-plans';
		const { data } = await this.getInstance().get<ActionPlansResponseDTO>(url, { params });
		return data;
	}

	async getErgonomicTools(params: GetErgonomicToolsRequestDTO): Promise<GetErgonomicToolsResponseDTO> {
		const url = this.basePath + '/result/ergonomic-tools';
		const { data } = await this.getInstance().get<GetErgonomicToolsResponseDTO>(url, { params });
		return new GetErgonomicToolsMapper(data).toDomain();
	}

	// async getLastConsolidatedActionLog(
	// 	params: GetLastConsolidatedActionLogRequestDTO
	// ): Promise<LastConsolidatedActionLogResponseDTO> {
	// 	const url = this.basePath + `/result/action-logs/last-consolidated/${params.custom_report_result_id}`;
	// 	const { data } = await this.getInstance().get<LastConsolidatedActionLogResponseDTO>(url, { params });
	// 	return data;
	// }

	async getLastConsolidatedActionLog(
		params: GetLastConsolidatedActionLogRequestDTO
	): Promise<GetLastConsolidatedActionLogResponseDTO> {
		const url = this.basePath + `/result/action-logs/last-consolidated/${params.custom_report_result_id}`;
		const { data } = await this.getInstance().get<GetLastConsolidatedActionLogResponseDTO>(url, { params });
		return data;
	}
}

const Service = Object.freeze(new CustomReportService());
export { Service };
